import axios from 'axios';
import { CompositionConflict, type CompositionApi } from './sync';
import type { CompositionDocument } from './document';

export function createCompositionApi(
  baseUrl: string,
  token: () => Promise<string | undefined>,
): CompositionApi {
  const endpoint = `${baseUrl.replace(/\/$/, '')}/api/v1/compositions`;
  const config = async () => {
    const auth = await token();
    if (!auth)
      throw new Error('Sign in again to sync your compositions. Your local sketches are safe.');
    return { headers: { Authorization: `Bearer ${auth}` }, timeout: 15000 };
  };
  return {
    async history(id) {
      return (
        await axios.get<{ items: CompositionDocument[] }>(
          `${endpoint}/${id}/revisions`,
          await config(),
        )
      ).data.items;
    },
    async list(after) {
      return (await axios.get(endpoint, { ...(await config()), params: after ? { after } : {} }))
        .data;
    },
    async get(id) {
      return (
        await axios.get<{ document: CompositionDocument }>(`${endpoint}/${id}`, await config())
      ).data.document;
    },
    async put(request) {
      try {
        const { cloud: _cloud, ...document } = request.document;
        return (
          await axios.put<{ document: CompositionDocument }>(
            `${endpoint}/${request.id}`,
            {
              document,
              operationId: request.operationId,
              expectedRevision: request.expectedRevision,
            },
            await config(),
          )
        ).data.document;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const response = error.response;
          if (
            response?.status === 409 &&
            ['REVISION_CONFLICT', 'TAKE_CONFLICT'].includes(response.data?.code) &&
            response.data.document
          ) {
            throw new CompositionConflict(response.data.document);
          }
          if (response?.status === 404)
            throw new Error(
              'Cloud composition storage is not available yet. Your sketches are saved on this device.',
            );
          if (response?.status === 413)
            throw new Error(
              'This composition exceeds the cloud storage limit. Export a backup to keep a separate copy.',
            );
        }
        throw error;
      }
    },
  };
}
